/**
 * Exports API service functions
 * Handles Excel and PDF export operations
 */
import { buildConfigApiUrl } from '@api/api';
import { handleApiResponse } from '@common/utils/apiError';

/**
 * PDF export orientation options
 */
export const PDF_ORIENTATION = {
  PORTRAIT: 'portrait',
  LANDSCAPE: 'landscape',
};

/**
 * PDF page size options
 */
export const PDF_PAGE_SIZE = {
  A4: 'A4',
  A3: 'A3',
  LETTER: 'Letter',
  LEGAL: 'Legal',
};

/**
 * Extract filename from Content-Disposition header
 * @param {Response} response - Fetch response
 * @param {string} defaultFilename - Default filename if not found in header
 * @returns {string} - Extracted or default filename
 */
function extractFilename(response, defaultFilename) {
  const contentDisposition = response.headers.get('Content-Disposition');
  if (contentDisposition) {
    const filenameMatch = contentDisposition.match(/filename="?([^";\n]+)"?/);
    if (filenameMatch) {
      return filenameMatch[1];
    }
  }
  return defaultFilename;
}

/**
 * Export dashboard to PDF
 * @param {Object} exportData - The export request data
 * @param {number} exportData.dashboardId - ID of the dashboard to export
 * @param {Object} exportData.filters - Applied filters
 * @param {string} exportData.userName - Name of the user exporting
 * @param {string} exportData.savedReportName - Name of the saved report (optional)
 * @param {Object} exportData.widgetChartOptions - Pre-built ECharts option objects keyed by widget ID (optional)
 * @param {Object} exportData.options - PDF export options (optional)
 * @param {string} exportData.options.orientation - 'portrait' or 'landscape'
 * @param {string} exportData.options.pageSize - 'A4', 'A3', 'Letter', or 'Legal'
 * @param {boolean} exportData.options.includeFilters - Include filters summary
 * @param {boolean} exportData.options.includeHeader - Include header with title/date
 * @returns {Promise<{blob: Blob, filename: string}>} - PDF file as Blob with filename
 */
export const exportToPdf = async (exportData) => {
  const response = await fetch(buildConfigApiUrl('/dashboards/export/pdf'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      dashboardId: exportData.dashboardId,
      filters: exportData.filters || {},
      userName: exportData.userName || 'Unknown',
      savedReportName: exportData.savedReportName || null,
      widgetChartOptions: exportData.widgetChartOptions,
      options: exportData.options || null,
    }),
  });

  await handleApiResponse(response);

  const filename = extractFilename(response, 'report.pdf');
  const blob = await response.blob();
  return { blob, filename };
};

/**
 * Download PDF export
 * Triggers browser download for the PDF file
 * @param {Object} exportData - The export request data
 * @param {number} exportData.dashboardId - ID of the dashboard to export
 * @param {Object} exportData.filters - Applied filters
 * @param {string} exportData.userName - Name of the user exporting
 * @param {string} exportData.savedReportName - Name of the saved report (optional)
 */
export const downloadPdfExport = async (exportData) => {
  const { blob, filename } = await exportToPdf(exportData);

  // Create download link
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;

  // Trigger download
  document.body.appendChild(link);
  link.click();

  // Cleanup
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/**
 * Export dashboard to Excel
 * @param {Object} exportData - The export request data
 * @param {number} exportData.dashboardId - ID of the dashboard to export
 * @param {Object} exportData.filters - Applied filters
 * @param {string} exportData.userName - Name of the user exporting
 * @param {string} exportData.savedReportName - Name of the saved report (optional)
 * @param {Object} exportData.widgetChartOptions - Pre-built ECharts option objects keyed by widget ID (optional)
 * @returns {Promise<{blob: Blob, filename: string}>} - Excel file as Blob with filename
 */
export const exportToExcel = async (exportData) => {
  const response = await fetch(buildConfigApiUrl('/dashboards/export/excel'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      dashboardId: exportData.dashboardId,
      filters: exportData.filters || {},
      userName: exportData.userName || 'Unknown',
      savedReportName: exportData.savedReportName || null,
      widgetChartOptions: exportData.widgetChartOptions,
    }),
  });

  await handleApiResponse(response);

  const filename = extractFilename(response, 'report.xlsx');
  const blob = await response.blob();
  return { blob, filename };
};

/**
 * Download Excel export
 * Triggers browser download for the Excel file
 * @param {Object} exportData - The export request data
 * @param {number} exportData.dashboardId - ID of the dashboard to export
 * @param {Object} exportData.filters - Applied filters
 * @param {string} exportData.userName - Name of the user exporting
 * @param {string} exportData.savedReportName - Name of the saved report (optional)
 */
export const downloadExcelExport = async (exportData) => {
  const { blob, filename } = await exportToExcel(exportData);

  // Create download link
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;

  // Trigger download
  document.body.appendChild(link);
  link.click();

  // Cleanup
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

